import { useState } from 'react';
import { CaretDown, Check, X, Trash } from '@phosphor-icons/react';
import ConfirmDialog from './ConfirmDialog';
import { useT } from '../i18n/I18nContext';
import { labelColor } from '../lib/labels';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const VISIBLE_LABELS = 3;

// Board header label filter: the most-used labels as chips, the rest behind a
// "+N" menu. Active labels always stay visible as chips so a filter is never hidden.
// ``labels`` is [{ name, count }] sorted most-used first.
export default function LabelFilter({ labels, activeLabels, onToggle, onDelete }) {
  const t = useT();
  const [menuOpen, setMenuOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const top = labels.slice(0, VISIBLE_LABELS);
  const overflow = labels.slice(VISIBLE_LABELS);
  const chips = [...top, ...overflow.filter((l) => activeLabels.has(l.name))];
  const hidden = overflow
    .filter((l) => !activeLabels.has(l.name))
    .sort((a, b) => a.name.localeCompare(b.name));

  const askDelete = (label) => {
    setMenuOpen(false);
    setPendingDelete(label);
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await onDelete(pendingDelete.name);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="flex items-center gap-1 flex-wrap min-w-0" data-testid="board-label-filter">
      {chips.map((l) => {
        const active = activeLabels.has(l.name);
        return (
          <span key={l.name} className="group/label relative inline-flex">
            <button
              type="button"
              data-testid={`board-label-filter-${l.name}`}
              onClick={() => onToggle(l.name)}
              title={`${l.name} · ${l.count}`}
              className={`text-[10px] font-mono px-1.5 py-0.5 border rounded-sm transition-all ${labelColor(l.name)} ${
                active ? 'ring-1 ring-offset-1 ring-zinc-900' : 'opacity-60 hover:opacity-100'
              }`}
              aria-pressed={active}
            >
              {l.name}
            </button>
            <button
              type="button"
              data-testid={`board-label-delete-${l.name}`}
              onClick={() => askDelete(l)}
              title={t('projectBoard.deleteLabelTitle')}
              aria-label={`${t('projectBoard.deleteLabelTitle')}: ${l.name}`}
              className="absolute -top-1.5 -right-1.5 w-3.5 h-3.5 rounded-full bg-zinc-900 text-white items-center justify-center hidden group-hover/label:flex focus-visible:flex hover:bg-red-600"
            >
              <X size={8} weight="bold" />
            </button>
          </span>
        );
      })}

      {hidden.length > 0 && (
        <DropdownMenu modal={false} open={menuOpen} onOpenChange={setMenuOpen}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              data-testid="board-label-more"
              title={t('projectBoard.moreLabels')}
              className="inline-flex items-center gap-0.5 text-[10px] font-mono px-1.5 py-0.5 border border-zinc-200 rounded-sm text-zinc-600 hover:border-zinc-400 hover:text-zinc-900"
            >
              +{hidden.length}
              <CaretDown size={9} weight="bold" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="z-[100] w-56 rounded-sm max-h-[50vh] overflow-y-auto">
            {hidden.map((l) => (
              <DropdownMenuItem
                key={l.name}
                data-testid={`board-label-option-${l.name}`}
                // Keep the menu open so several labels can be picked in a row.
                onSelect={(e) => {
                  e.preventDefault();
                  onToggle(l.name);
                }}
                className="group/option rounded-sm gap-2"
              >
                <Check size={12} weight="bold" className="invisible" />
                <span className={`text-[10px] font-mono px-1.5 py-0.5 border rounded-sm truncate ${labelColor(l.name)}`}>
                  {l.name}
                </span>
                <span className="ml-auto font-mono text-[10px] tabular-nums text-zinc-400 group-hover/option:hidden">
                  {l.count}
                </span>
                <button
                  type="button"
                  data-testid={`board-label-option-delete-${l.name}`}
                  title={t('projectBoard.deleteLabelTitle')}
                  aria-label={`${t('projectBoard.deleteLabelTitle')}: ${l.name}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    askDelete(l);
                  }}
                  className="ml-auto p-0.5 rounded-sm text-zinc-400 hover:text-red-600 hover:bg-red-50 hidden group-hover/option:inline-flex"
                >
                  <Trash size={12} weight="bold" />
                </button>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(v) => { if (!v) setPendingDelete(null); }}
        title={t('projectBoard.deleteLabelConfirmTitle', { name: pendingDelete?.name ?? '' })}
        description={t('projectBoard.deleteLabelConfirmDescription', { count: pendingDelete?.count ?? 0 })}
        confirmLabel={t('projectBoard.deleteLabelConfirmBtn')}
        variant="destructive"
        loading={deleting}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
